import { createField } from "./fieldsFactory";
import { DiscoverConstants } from "./discoverConstants";
import { Countries } from "./countries";

export const DiscoverFields = {
  accountClosed: createField({
    label: "Account closed",
    name: "accountClosed",
    fieldType: "Boolean",
    description: "This field indicates if the Card account has been closed"
  }),
  acquirerReferenceNumber: createField({
    label: "Acquirer reference number",
    name: "acquirerReferenceNumber",
    fieldType: "String",
    description:
      "This field contains the id for the transaction that was assigned by the Acquirer."
  }),
  actionCategoryDescription: createField({
    label: "Action category description",
    name: "actionCategoryDescription",
    fieldType: "String",
    description:
      "This field contains the follow up action category description. It is returned with the case history item when using the case details message."
  }),
  actionCode: createField({
    label: "Action codes",
    name: "actionCode",
    fieldType: "String",
    fixedLength: 5,
    description:
      "This field contains a value that uniquely identifies a follow up action."
  }),
  actionDate: createField({
    label: "Action date",
    name: "actionDate",
    fieldType: "Date",
    description: "This field contains the date the action was taken."
  }),
  actionName: createField({
    label: "Action name",
    name: "actionName",
    maxLength: 30,
    fieldType: "String",
    description:
      "This field contains the name of the follow up action that was performed. This is returned as part of the case history when using the case details message."
  }),
  approvalCode: createField({
    label: "Apporval code",
    name: "approvalCode",
    fixedLength: 5,
    fieldType: "String",
    description:
      "The code provided by Discover by an Issuer, or by Discover in certain circumstances, in an Authorization Response, which Discover delivers to the Merchant Acquirer for Acquirer's delivery to the Merchant, which indicates that the Issuer, or Discover in certain circumstances, provided a positive or approved decision in response to the Authorization Request. This code is also referred to as the Approval Code."
  }),
  authorizationApproved: createField({
    label: "Authorization approved",
    name: "authorizationApproved",
    fieldType: "Boolean",
    description:
      "This field contains a true or false value to determine if the Authorization of the transaction was approved. If true, the transaction was approved. If false, the transaction was not approved."
  }),
  authorizationServiceCode: createField({
    label: "Authorization service code",
    name: "authorizationServiceCode",
    fieldType: "Array",
    fieldValues: DiscoverConstants.AuthorizationServiceCodes,
    description:
      "This field contains the Service Code encoded on Track 1 of the Card"
  }),
  avsValidated: createField({
    label: "AVS Validated",
    name: "avsValidated",
    fieldType: "Boolean",
    description:
      "This field contains a true or false value to determine if the Address Verification Service (AVS) was validated at the time of the transaction. If true, the AVS was validated. If false, the AVS was not validated."
  }),
  benCaseIndicator: createField({
    label: "BEN case indicator",
    name: "benCaseIndicator",
    fieldType: "Boolean",
    description:
      "This field contains a true or false value to determine if the Dispute case has a Billing Error Notice (BEN). If true, the Dispute case is a BEN case. If false, the Dispute Case is not a BEN case."
  }),
  benDueDate: createField({
    label: "BEN due date",
    name: "benDueDate",
    fieldType: "Date",
    description:
      "This field contains date in which the Dispute for the Billing Error Notice (BEN) must be completed."
  }),
  benReceivedDate: createField({
    label: "BEN received date",
    name: "benReceivedDate",
    fieldType: "Date",
    maxDate: 0,
    description:
      "This field contains the date when the Billing Error Notice (BEN) is to be received."
  }),
  cancellationDate: createField({
    label: "Cancellation date",
    name: "cancellationDate",
    fieldType: "Date",
    maxDate: 0,
    description:
      "This field contains the date when the merchandise or service was cancelled."
  }),
  cancellationHandlerName: createField({
    label: "Cancellation handler name",
    name: "cancellationHandlerName",
    maxLength: 30,
    fieldType: "String",
    description:
      "This field contains the name of the person who handled the cancellation."
  }),
  cancellationHandlerNumber: createField({
    label: "Cancellation handler number",
    name: "cancellationHandlerNumber",
    minLength: 7,
    maxLength: 15,
    fieldType: "String",
    fieldType2: "PhoneNumber",
    description: "This field contains the phone number that was used to cancel."
  }),
  cancellationMethod: createField({
    label: "Cancellation method",
    name: "cancellationMethod",
    maxLength: 30,
    fieldType: "Array",
    fieldValues: DiscoverConstants.CancellationMethods,
    description:
      "This field contains the method of cancellation of the transactions."
  }),
  cancellationNumber: createField({
    label: "Cancellation number",
    name: "cancellationNumber",
    maxLength: 20,
    fieldType: "String",
    description:
      "This field contains the number associated with the cancellation provided by the handler."
  }),
  cancellationOrReturnReason: createField({
    label: "Cancellation or Return reason",
    name: "cancellationOrReturnReason",
    maxLength: 40,
    fieldType: "String",
    description:
      "This field contains any text that helps to describe the reason behind the cancellation."
  }),
  cancelledOrReturned: createField({
    label: "Cancelled or Returned",
    name: "cancelledOrReturned",
    maxLength: 30,
    fieldType: "Array",
    fieldValues: DiscoverConstants.CancelledOrReturned,
    description:
      "This field contains a value of either C or R to signify if the merchandise or service was returned or cancelled."
  }),
  cardholderAddress: createField({
    label: "Address",
    name: "cardholderAddress",
    maxLength: 40,
    fieldType: "String",
    description:
      "This field contains the street address of the person in which the Card was issued."
  }),
  cardholderCity: createField({
    label: "City",
    name: "cardholderCity",
    maxLength: 28,
    fieldType: "String",
    description:
      "This field contains the City of the person in which the Card was issued."
  }),
  cardholderCountryCode: createField({
    label: "Country",
    name: "cardholderCountryCode",
    fieldType: "Array",
    fieldValues: Countries,
    description:
      "This field contains the Country Code of the person in which the Card was issued"
  }),
  cardholderName: createField({
    label: "Name",
    name: "cardholderName",
    maxLength: 48,
    fieldType: "String",
    description:
      "This field contains name of the person in which that Card was issued."
  }),
  cardholderPhoneNumber: createField({
    label: "Phone number",
    name: "cardholderPhoneNumber",
    fieldType: "String",
    fieldType2: "PhoneNumber",
    description:
      "This field contains the Phone Number of the person in which the Card was issued."
  }),
  cardholderPostalCode: createField({
    label: "Postal code",
    name: "cardholderPostalCode",
    maxLength: 15,
    fieldType: "String",
    description:
      "This field contains the Postal Code of the person in which the Card was issued."
  }),
  cardholderState: createField({
    label: "State",
    name: "cardholderState",
    fieldType: "String",
    description:
      "This field contains the State of the person in which the Card was issued."
  }),
  cardInPossession: createField({
    label: "Card in possession",
    name: "cardInPossession",
    fieldType: "Boolean",
    description:
      "The field contains a true or false value on whether the Cardholder has the card in their possession."
  }),
  cardNumber: createField({
    label: "PAN",
    name: "cardNumber",
    minLength: 10,
    maxLength: 19,
    fieldType: "String",
    description:
      "This field contains the Cardholder's account number associated with the transaction. This is used as a key to search for transactions to Dispute."
  }),
  cardPresentAtTransactionTime: createField({
    label: "Card present at transaction time",
    name: "cardPresentAtTransactionTime",
    fieldType: "Boolean",
    description:
      "This field contains a true or false value on whether or not the card was present at the time of the transaction."
  }),
  caseClosedDate: createField({
    label: "Card closed date",
    name: "caseClosedDate",
    fieldType: "Date",
    description: "This field holds the date that the case was last closed."
  }),
  caseExists: createField({
    label: "Card exists",
    name: "caseExists",
    fieldType: "Boolean",
    description:
      "This field contains a true or false value that indicates if a case already exists for request being submitted."
  }),
  caseInFavorOf: createField({
    label: "Case in favor of",
    name: "caseInFavorOf",
    fieldType: "Array",
    fieldValues: DiscoverConstants.CaseInFavorOf,
    description:
      "This field contains a code value that determines if the case is currently in favor of the Cardholder, the Merchant or is awaiting a decision from the network."
  }),
  caseNote: createField({
    label: "Case note",
    name: "caseNote",
    maxLength: 750,
    fieldType: "String",
    description:
      "This field holds any additional text that should be included with the Dispute case."
  }),
  caseNoteDate: createField({
    label: "Case note date",
    name: "caseNoteDate",
    fieldType: "Date",
    description: "This field holds the date that the case note was created."
  }),
  caseNoteUserId: createField({
    label: "Case note user ID",
    name: "caseNoteUserId",
    minLength: 7,
    maxLength: 25,
    fieldType: "String",
    description:
      "This field will contain the user's login id for the person who created the case note."
  }),
  caseNoteUserName: createField({
    label: "Case note user name",
    name: "caseNoteUserName",
    minLength: 8,
    maxLength: 50,
    fieldType: "String",
    description:
      "This field will contain the user's name of the person who created the case note"
  }),
  caseStatus: createField({
    label: "Case status",
    name: "caseStatus",
    fieldType: "Array",
    fieldValues: DiscoverConstants.CaseStatuses,
    description:
      "This field contains a value indicating that the case is open or closed."
  }),
  caseStatusDescription: createField({
    label: "Case status description",
    name: "caseStatusDescription",
    fieldType: "Array",
    fieldValues: DiscoverConstants.CaseStatuses,
    description: "This field contains a description of the case status."
  }),
  cashOverAmount: createField({
    label: "Cash over amount",
    name: "cashOverAmount",
    fieldType: "Number",
    fieldType2: "Money",
    minValue: 0.0,
    maxValue: 9999999.99,
    description:
      "This field contains the amount of the Cash Over that is subject to dispute. The amount is reflected in the Issuer’s settlement currency of record with Discover."
  }),
  cashOverCorrectAmount: createField({
    label: "Cash over correct amount",
    name: "cashOverCorrectAmount",
    fieldType: "Number",
    fieldType2: "Money",
    minValue: 0.0,
    maxValue: 9999999.99,
    description:
      "This field contains the consented amount of the Cash Over portion of the Card Transaction, The amount is reflected in the Issuer’s settlement currency of record with Discover."
  }),
  cashOverOption: createField({
    label: "Cash over option",
    name: "cashOverOption",
    fieldType: "Array",
    fieldValues: DiscoverConstants.CashOverOptions,
    defaultValue: DiscoverConstants.CashOverOptions[3].value,
    description:
      "This field contains a value identifying the portion of the Card Transaction that is subject to dispute."
  }),
  cashOverReason: createField({
    label: "Cash over reason",
    name: "cashOverReason",
    maxLength: 500,
    fieldType: "String",
    description:
      "This field contains a description of why the cash over portion is in dispute."
  }),
  changeReasonOrAmount: createField({
    label: "Change Reason or Amount?",
    name: "changeReasonOrAmount",
    fieldType: "Boolean",
    description:
      "This field contains a true or false value to determine if the Reason Code is being changed. If true, the Reason Code is being changed. If false, the Reason Code is not being changed."
  }),
  cidValidated: createField({
    label: "CID Validated",
    name: "cidValidated",
    fieldType: "Boolean",
    description:
      "This field contains a true or false value to determine if the Card Identification Data (CID) was validated during the transaction. If the value is true, the CID was validated. If false, the CID was not validated."
  }),
  commentDate: createField({
    label: "Comment date",
    name: "commentDate",
    fieldType: "Date",
    fieldType2: "DateTime",
    description:
      "This field contains the date and time a comment on the case has been added."
  }),
  confirmedFraudFile: createField({
    label: "Confirmed fraud file",
    name: "confirmedFraudFile",
    fieldType: "Boolean",
    description:
      "This field indicates if the Card has been added to the Confirmed Fraud File."
  }),
  correctAmount: createField({
    label: "Correct amount",
    name: "correctAmount",
    fieldType: "Number",
    fieldType2: "Money",
    minValue: 0.0,
    maxValue: 9999999.99,
    description:
      "This field contains the amount the Cardholder agreed to at the time of the transaction. The amount is reflected in the Issuer’s settlement currency of record with Discover."
  }),
  counterfeitConfirmationDate: createField({
    label: "Counterfeit confirmation date",
    name: "counterfeitConfirmationDate",
    fieldType: "Date",
    description:
      "This field contains that date when the merchandise was confirmed as counterfeit."
  }),
  creditReceiptDate: createField({
    label: "Credit receipt date",
    name: "creditReceiptDate",
    fieldType: "Date",
    maxDate: 0,
    description: "This field contains the date on the Credit receipt."
  }),
  creditResolvesCase: createField({
    label: "Credit resolves case",
    name: "creditResolvesCase",
    fieldType: "Boolean",
    description:
      "This field contains a true or false value to determine if the credit issued resolves the Dispute."
  }),
  cycleCloseDate: createField({
    label: "Cycle close date",
    name: "cycleCloseDate",
    fieldType: "Date",
    description:
      "This field contains the date by which the next action needs to be performed."
  }),
  cycleCreationDate: createField({
    label: "Cycle creation date",
    name: "cycleCreationDate",
    fieldType: "Date",
    description:
      "This field contains the date in which the action was performed."
  }),
  dateOfViolation: createField({
    label: "Date of violation",
    name: "dateOfViolation",
    fieldType: "Date",
    maxDate: 0,
    description:
      "This field will contain the date on which the transaction occurred and is used when searching for a transaction to Dispute."
  }),
  daysInDispute: createField({
    label: "Days in dispute",
    name: "daysInDispute",
    fieldType: "Number",
    fieldType2: "Integer",
    description: "The number of days that the current case has been open."
  }),
  disputeAmount: createField({
    label: "Dispute amount",
    name: "disputeAmount",
    fieldType: "Number",
    fieldType2: "Money",
    minValue: 0.0,
    maxValue: 9999999.99,
    caption: "If not entered, settlement amount will be used",
    description:
      "This field contains the amount of the Dispute in the Issuer’s settlement currency of record with Discover."
  }),
  disputeMotiveId: createField({
    label: "Dispute motive ID",
    name: "disputeMotiveId",
    fieldType: "String",
    fixedLength: 2,
    description:
      "This field contains the motive id, if applicable, to describe the reason behind the Dispute."
  }),
  disputeMotiveIdDescription: createField({
    label: "Dispute motive ID description",
    name: "disputeMotiveIdDescription",
    fieldType: "String",
    maxLength: 100,
    description: "This field contains a description of the motive"
  }),
  dnDisputeCaseNumber: createField({
    label: "DN dispute case number",
    name: "dnDisputeCaseNumber",
    fieldType: "Number",
    fieldType2: "Integer",
    fixedLength: 10,
    description:
      "This field is a unique number assigned by Discover that references a Dispute case."
  }),
  dndsTransactionKey: createField({
    label: "DNDS transaction key",
    name: "dndsTransactionKey",
    fieldType: "String",
    description:
      "This field contains an encrypted key value provided by Discover that can be retrieved from the transaction search. This field is used as the key to the transaction in the other interactions, in place of the transaction search values."
  }),
  docType: createField({
    label: "Document type",
    name: "docType",
    fieldType: "Array",
    fieldValues: DiscoverConstants.DocTypes,
    description:
      "This field identifies the type of documentation that is being provided for the Dispute case."
  }),
  documentationIndicator: createField({
    label: "Documentation indicator",
    name: "documentationIndicator",
    fieldType: "Array",
    fieldValues: DiscoverConstants.DocumentationIndicators,
    description:
      "This field contains a value that indicates if supporting documentation will be provided."
  }),
  documentId: createField({
    label: "Document ID",
    name: "documentId",
    fieldType: "String",
    description:
      "This field contains the unique key value to a document image in the Discover Network Disputes System."
  }),
  documentOwner: createField({
    label: "Document owner",
    name: "documentOwner",
    fieldType: "Array",
    fieldValues: DiscoverConstants.DocumentOwners,
    description:
      "This field indicates if the document was submitted to Discover by the Acquirer or by the Issuer."
  }),
  documentTimestamp: createField({
    label: "Document timestamp",
    name: "documentTimestamp",
    fieldType: "Date",
    fieldType2: "DateTime",
    description:
      "This field contains the date and time at which Discover Disputes received a document image."
  }),
  duplicateSearchOption: createField({
    label: "Duplicate search options",
    name: "duplicateSearchOption",
    fieldType: "Array",
    fieldValues: DiscoverConstants.DuplicateSearchOptions,
    description:
      "This field contains a value instructing how to match transactions for disputes cases involving multiple transactions."
  }),
  entryDate: createField({
    label: "Entry date",
    name: "entryDate",
    fieldType: "Date",
    description:
      "This field contains the entry date of the case history items that are returned when viewing the case details."
  }),
  errorText: createField({
    label: "Error text",
    name: "errorText",
    fieldType: "String",
    description:
      "This field contains a description of the error that has occurred during the iteration."
  }),
  fileFormat: createField({
    label: "File format",
    name: "fileFormat",
    fieldType: "Array",
    fieldValues: DiscoverConstants.FileFormats,
    description:
      "This field contains the file format that is being passed into the store case document message."
  }),
  fraud: createField({
    label: "Fraud",
    name: "fraud",
    fieldType: "Boolean",
    description: "This field indicates if the Card status is Fraud."
  }),
  image: createField({
    label: "Upload Document",
    name: "image",
    fieldType: "File",
    fieldType2: "Image",
    acceptedFormats: [".pdf"],
    description: "This field contains an image of a document.",
    maxFileSize: 500000
  }),
  issuerCaseNumber: createField({
    label: "Issue case number",
    name: "issuerCaseNumber",
    fieldType: "String",
    minLength: 1,
    maxLength: 20,
    description:
      "This field will contain the case identification number that the Issuer has assigned the case."
  }),
  caseNumber: createField({
    label: "Case number",
    name: "caseNumber",
    fieldType: "String",
    minLength: 1,
    maxLength: 20,
    description: "This field is a unique number that references a Dispute case."
  }),
  issuerInPossessionOfCard: createField({
    label: "Issuer in possession of card",
    name: "issuerInPossessionOfCard",
    fieldType: "Boolean",
    description:
      "This field contains a true or false value to determine if the Issuer has possession of the card. If true, the Issuer does have possession of the card. If false, the Issuer does not have possession of the card."
  }),
  isTransactionDisputed: createField({
    label: "Is transaction disputed",
    name: "isTransactionDisputed",
    fieldType: "Boolean",
    description:
      "This field contains a true or false value to determine if the transaction has been disputed. If true, the transaction has been disputed. If false, the transaction has not been disputed."
  }),
  memberId: createField({
    label: "Member ID",
    name: "memberId",
    fieldType: "Number",
    fixedLength: 42,
    fieldType2: "Integer",
    description:
      "This field is provided by Discover and contains a unique id for the Client who is calling the WebService. It is used during authentication."
  }),
  merchandiseCorrectAmount: createField({
    label: "Merchandise correct amount",
    name: "merchCorrectAmount",
    fieldType: "Number",
    fieldType2: "Money",
    minValue: 0.0,
    maxValue: 9999999.99,
    description:
      "This field identifies the consented amount of the merchandise portion of the Card Transaction amount where the transaction also contained Cash Over. The amount is reflected in the Issuer’s settlement currency of record with Discover."
  }),
  merchandiseDescription: createField({
    label: "Merchandise description",
    name: "merchandiseDescription",
    fieldType: "String",
    maxLength: 40,
    description:
      "This field contains text describing the merchandise purchased in Non Receipt of Goods or Services disputes."
  }),
  merchandiseServiceDescription: createField({
    label: "Merchandise service description",
    name: "merchandiseServiceDescription",
    fieldType: "String",
    maxLength: 40,
    description:
      "This field contains a text description of the merchandise or service purchased in a Credit Not Processed dispute."
  }),
  merchantCategoryCode: createField({
    label: "Merchant category",
    name: "merchantCategoryCode",
    fieldType: "String",
    description: "This field contains a code describing the type of Merchant."
  }),
  merchantCategoryCodeDescription: createField({
    label: "Merchant category",
    name: "merchantCategoryCodeDescription",
    fieldType: "String",
    description: "This field contains a description of the type of Merchant."
  }),
  merchantName: createField({
    label: "Merchant name",
    name: "merchantName",
    fieldType: "String",
    description: "This field contains the name of the Merchant."
  }),
  merchantNumber: createField({
    label: "Merchant number",
    maxLength: 15,
    name: "merchantNumber",
    fieldType: "Number",
    description:
      "This field contains a unique number that identifies a particular Merchant. It is a key that is used to search for transactions to Dispute."
  }),
  merchantPostDate: createField({
    label: "Merchant post date",
    name: "merchantPostDate",
    fieldType: "Date",
    description:
      "This field contains the date that the Merchant posted the transaction to Discover."
  }),
  messageId: createField({
    label: "Message ID",
    name: "messageId",
    fieldType: "String",
    maxLength: 36,
    description:
      "This field contains a unique ID that identifies each Message that is being sent. This ID is assigned by the Issuer upon initiating the interaction. It is used to prevent processing of duplicate transmissions."
  }),
  noticeComment: createField({
    label: "Notice comment",
    name: "noticeComment",
    fieldType: "String",
    maxLength: 500,
    description:
      "This field contains any additional comments to be added to the notice."
  }),
  noticeTypeCode: createField({
    label: "Notice type",
    name: "noticeTypeCode",
    fieldType: "Array",
    fieldValues: DiscoverConstants.NoticeTypeCodes,
    description:
      "The field contains the type of notice that will or has been created. This comes back on the response of create case, follow up action and case detail messages. It is passed into the request to store a document on the case."
  }),
  noticeTypeCodeDescription: createField({
    label: "Notice type description",
    name: "noticeTypeCodeDescription",
    fieldType: "Array",
    fieldValues: DiscoverConstants.NoticeTypeCodes,
    description: "The field contains the description of the Notice Type code."
  }),
  NRID: createField({
    label: "NRID",
    name: "nrid",
    fieldType: "String",
    fixedLength: 15,
    description:
      "This field contains the unique network reference ID for a transaction. It is an optional field used to search for a transaction to Dispute."
  }),
  orderCancelled: createField({
    label: "Order cancelled",
    name: "orderCancelled",
    fieldType: "Boolean",
    description:
      "This field will indicate if the order has been cancelled. If true, the order has been cancelled. If false, the order has not been cancelled. This is used as part of a Non Receipt of Goods or Services dispute."
  }),
  origCurrencyAmount: createField({
    label: "ORIG currency amount",
    name: "origCurrencyAmount",
    fieldType: "Number",
    fieldType2: "Money",
    minValue: 0.0,
    maxValue: 9999999.99,
    description:
      "This field is the total Card Transaction amount in the Originating Currency in which the Card Transaction is conducted by the Merchant. This amount includes any Cash Over, taxes, and/or gratuities. This field contains the total Card Transaction Amount represented in the Sales Data File sent to the Discover."
  }),
  origCurrencyCode: createField({
    label: "ORIG currency code",
    name: "origCurrencyCode",
    fieldType: "String",
    fixedLength: 3,
    description:
      "This field indicates the code assigned to the Originating Currency represented in Sales Data for the Card Transaction conducted by the Merchant."
  }),
  pinEnabled: createField({
    label: "Pin enabled",
    name: "pinEnabled",
    fieldType: "Boolean",
    description: "This field indicates if the Card is a PIN enabled Chip Card."
  }),
  priorAuthorizationWithAvsAndCid: createField({
    label: "Prior authorization with AVS and CID",
    name: "priorAuthorizationWithAvsAndCid",
    fieldType: "Boolean",
    description:
      "This field contains a true or false value that indicates if the transaction had prior Authorization with Address Verification Service (AVS) and the Card Identification Data(CID). If true, the transaction had Authorization with these methods. If false, the transaction did not have Authorization with these methods.."
  }),
  processorID: createField({
    label: "Processor ID",
    name: "processorID",
    fieldType: "String",
    description:
      "This field contains the unique id for the processor of a transaction"
  }),
  promisedDeliveryDate: createField({
    label: "Promised delivery date",
    name: "promisedDeliveryDate",
    fieldType: "Date",
    description:
      "This field will contain the promised delivery date for the good and/or serviced to be delivered in Non Receipt of Goods or Services Disputes."
  }),
  reason: createField({
    label: "Reason",
    name: "reason",
    fieldType: "String",
    description:
      "This field contains text explaining why the merchandise or service was returned or cancelled. This is used in Credit Not Processed Disputes."
  }),
  reasonCode: createField({
    label: "Reason code",
    name: "reasonCode",
    fieldType: "String",
    description:
      "This field contains the Reason Code that represents the Dispute case type."
  }),
  reasonDescription: createField({
    label: "Reason description",
    name: "reasonDescription",
    fieldType: "String",
    description:
      "This field contains a description of the Reason Code variable."
  }),
  reservationDate: createField({
    label: "Reservation date",
    name: "reservationDate",
    fieldType: "Date",
    maxDate: 0,
    description:
      "This field contains the date of a reservation used in Cancelled Reservation disputes."
  }),
  responseCode: createField({
    label: "Response code",
    name: "responseCode",
    fixedLength: 33,
    fieldType: "String",
    description:
      "This field will contain the 4 byte code that is returned with every response."
  }),
  responseCodeDescription: createField({
    label: "Response code description",
    name: "responseCodeDescription",
    maxLength: 500,
    fieldType: "String",
    description:
      "This field will contain the description of the response code that is returned with every response."
  }),
  responseCodeLevel: createField({
    label: "Response code level",
    name: "responseCodeLevel",
    fieldType: "Array",
    fieldValues: DiscoverConstants.ResponseCodeLevels,
    description:
      "This field will contain an indicator of the severity of the response."
  }),
  returnedDate: createField({
    label: "Return date",
    name: "returnedDate",
    fieldType: "Date",
    maxDate: 0,
    description:
      "This field will contain the return date of merchandise for Credit Not Processed disputes."
  }),
  returnMethod: createField({
    label: "Return method",
    name: "returnMethod",
    fieldType: "Array",
    fieldValues: DiscoverConstants.ReturnMethods,
    description:
      "This field will contain one of the enumerated values to describe the return method."
  }),
  reversalAmount: createField({
    label: "Reversal amount",
    name: "reversalAmount",
    fieldType: "Number",
    fieldType2: "Money",
    minValue: 0,
    maxValue: 9999999.99,
    description:
      "This field will contain the US Dollar amount of the Chargeback Reversal. The amount is reflected in the Issuer’s Settlement currency of record with Discover."
  }),
  roleClass: createField({
    label: "Role class",
    name: "roleClass",
    minLength: 4,
    maxLength: 4,
    fieldType: "String",
    description:
      "This field is provided by Discover and contains a value to define the user's role in the system. It is used during the authentication of the user."
  }),
  subStatusCode: createField({
    label: "Sub status code description",
    name: "subStatusCodeDescription",
    fixedLength: 4,
    fieldType: "Array",
    description:
      "This field will contain the various states of a case using the sub status code. This field will be returned on the Response of the created case, follow up action and case details messages."
  }),
  subStatusCodeDescription: createField({
    label: "Sub status code description",
    name: "subStatusCodeDescription",
    maxLength: 200,
    fieldType: "Array",
    description:
      "This field will contain the description of the sub status code."
  }),
  supportingDocs: createField({
    label: "Support docs provided",
    name: "supportingDocs",
    fieldType: "Boolean",
    description:
      "This field indicates if a supporting document was provided when performing a follow up action."
  }),
  transactionAmount: createField({
    label: "Transaction amount",
    name: "transactionAmount",
    fieldType: "Number",
    fieldType2: "Money",
    minValue: 10.0,
    maxValue: 9999999.99,
    description:
      "This field contains the transaction amount to use when searching for a transaction. This field can be various types of currencies. Amount should be entered in the Issuer’s Settlement currency of record with Discover."
  }),
  transactionCashOverAmount: createField({
    label: "Transaction cash over amount",
    name: "transactionCashOverAmount",
    fieldType: "Number",
    fieldType2: "Money",
    minValue: 0.0,
    maxValue: 9999999.99,
    description:
      "This field contains the amount of the Cash Over portion of the Card Transaction. The amount is reflected in the Issuer’s Settlement currency of record with Discover."
  }),
  transactionCode: createField({
    label: "Transaction code",
    name: "transactionCode",
    minLength: 1,
    maxLength: 4,
    fieldType: "String",
    description:
      "This field contains a code that describes the type of transaction."
  }),
  transactionDate: createField({
    label: "Transaction date",
    name: "transactionDate",
    fieldType: "Date",
    description:
      "This field will contain the date on which the transaction occurred and is used when searching for a transaction to Dispute."
  }),
  transactionDescription: createField({
    label: "Transaction description",
    name: "transactionDescription",
    fieldType: "String",
    description:
      "This field contains text that describes the type of transaction code."
  }),
  transactionId: createField({
    label: "Transaction ID",
    name: "transactionId",
    fieldType: "String",
    description: "This field contains the id for the transaction."
  }),
  get genericTransactionKey() {
    return createField({
      label: "Generic Transaction Key",
      name: "genericTransactionKey",
      fieldType: "ArrayOfFields",
      fields: [
        this.NRID,
        this.approvalCode,
        this.cardNumber,
        this.merchantNumber,
        this.transactionAmount,
        this.transactionDate
      ]
    });
  },
  get transactionLookupKey() {
    return createField({
      label: "Transaction lookup key",
      name: "transactionLookupKey",
      fieldType: "ArrayOfFields",
      fields: [this.genericTransactionKey, this.dndsTransactionKey]
    });
  }
};



// WEBPACK FOOTER //
// ./src/configurations/discoverConfigs/fields.js